import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Avatar, Box, Button, Card, CardContent, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton,
  InputAdornment, InputLabel, MenuItem, Select, Snackbar, Stack, Table,
  TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import CategoryIcon from "@mui/icons-material/Category";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import ImageInput from "../../components/ImageInput";
import { BRAND } from "../../theme";
import { CategoryVM } from "../../data/types";
import { toCategoryVM } from "../../data/mappers";
import {
  fetchCategories, createCategory, updateCategory, deleteCategory, CategoryInput,
} from "../../data/api";

const norm = (s: string) => s.trim().toLowerCase();

interface FormState { id?: number; name: string; image: string; parentId: number | ""; }
const EMPTY: FormState = { name: "", image: "", parentId: "" };

type Snack = { open: boolean; msg: string; sev: "success" | "error" | "info" };

const CategoriesPage: React.FC = () => {
  const [rows, setRows] = useState<CategoryVM[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [nameError, setNameError] = useState<string>("");

  const [confirm, setConfirm] = useState<{ open: boolean; kind: "delete" | "dedupe"; target?: CategoryVM }>({ open: false, kind: "delete" });
  const [snack, setSnack] = useState<Snack>({ open: false, msg: "", sev: "info" });

  const load = async () => {
    setLoading(true);
    try {
      setRows((await fetchCategories() || []).map(toCategoryVM));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // --- duplicate detection (by normalized name) ---
  const dupCounts = useMemo(() => {
    const m = new Map<string, number>();
    rows.forEach((r) => m.set(norm(r.name), (m.get(norm(r.name)) || 0) + 1));
    return m;
  }, [rows]);
  const isDup = (r: CategoryVM) => (dupCounts.get(norm(r.name)) || 0) > 1;
  const dupExtras = useMemo(() => {
    // the redundant rows to remove: per name group keep the lowest id, drop the rest
    const seen = new Map<string, number>();
    const extras: CategoryVM[] = [];
    [...rows].sort((a, b) => a.id - b.id).forEach((r) => {
      const k = norm(r.name);
      if (seen.has(k)) extras.push(r); else seen.set(k, r.id);
    });
    return extras;
  }, [rows]);

  const parents = useMemo(() => rows.filter((r) => !r.parentId), [rows]);
  const parentName = (id?: number | null) =>
    id == null ? "—" : (rows.find((r) => r.id === id)?.name ?? `#${id}`);

  const filtered = useMemo(() => {
    const s = norm(q);
    return s ? rows.filter((r) => norm(r.name).includes(s)) : rows;
  }, [rows, q]);

  // --- form helpers ---
  const openCreate = () => { setForm(EMPTY); setNameError(""); setDialogOpen(true); };
  const openEdit = (r: CategoryVM) => {
    setForm({ id: r.id, name: r.name, image: r.image || "", parentId: r.parentId ?? "" });
    setNameError("");
    setDialogOpen(true);
  };

  const duplicateName = (name: string, selfId?: number) =>
    rows.some((r) => norm(r.name) === norm(name) && r.id !== selfId);

  const onNameChange = (v: string) => {
    setForm((f) => ({ ...f, name: v }));
    setNameError(v.trim() && duplicateName(v, form.id) ? "A category with this name already exists." : "");
  };

  const save = async () => {
    const name = form.name.trim();
    if (!name) { setNameError("Name is required."); return; }
    if (duplicateName(name, form.id)) { setNameError("A category with this name already exists."); return; }
    setBusy(true);
    try {
      const payload: CategoryInput = {
        ...(form.id ? { id: form.id } : {}),
        name,
        image: form.image.trim() || null,
        parentId: form.parentId === "" ? null : Number(form.parentId),
      };
      if (form.id) await updateCategory(payload); else await createCategory(payload);
      setDialogOpen(false);
      setSnack({ open: true, msg: form.id ? "Category updated." : "Category created.", sev: "success" });
      await load();
    } catch {
      setSnack({ open: true, msg: "Save failed. Is the backend reachable at localhost:8081?", sev: "error" });
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async (r: CategoryVM) => {
    setBusy(true);
    try {
      await deleteCategory(r.id);
      setSnack({ open: true, msg: `Deleted "${r.name}".`, sev: "success" });
      await load();
    } catch {
      setSnack({ open: true, msg: "Delete failed. Is the backend reachable?", sev: "error" });
    } finally {
      setBusy(false);
      setConfirm({ open: false, kind: "delete" });
    }
  };

  const removeDuplicates = async () => {
    setBusy(true);
    let ok = 0, fail = 0;
    for (const r of dupExtras) {
      try { await deleteCategory(r.id); ok++; } catch { fail++; }
    }
    setBusy(false);
    setConfirm({ open: false, kind: "dedupe" });
    setSnack({
      open: true,
      sev: fail ? "error" : "success",
      msg: fail ? `Removed ${ok}, ${fail} failed.` : `Removed ${ok} duplicate categor${ok === 1 ? "y" : "ies"}.`,
    });
    await load();
  };

  const Thumb: React.FC<{ r: CategoryVM }> = ({ r }) => {
    const [bad, setBad] = useState(false);
    const img = (r.image || "").trim();
    const src = /^(data:|https?:|\/\/)/.test(img) ? img : img.startsWith("/") ? img : img ? `/images/${img}` : "";
    if (!src || bad) {
      return <Avatar variant="rounded" sx={{ bgcolor: "#eef3f0", color: BRAND.green }}><CategoryIcon fontSize="small" /></Avatar>;
    }
    return <Avatar variant="rounded" src={src} alt={r.name} onError={() => setBad(true)} sx={{ width: 40, height: 40 }} />;
  };

  return (
    <Box>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4">Categories</Typography>
          <Typography variant="body2" color="text.secondary">{rows.length} categories</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <TextField size="small" placeholder="Search categories" value={q} onChange={(e) => setQ(e.target.value)} sx={{ minWidth: 220 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add category</Button>
        </Stack>
      </Stack>

      {dupExtras.length > 0 && (
        <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" disabled={busy} onClick={() => setConfirm({ open: true, kind: "dedupe" })}>Remove duplicates</Button>}>
          {dupExtras.length} duplicate categor{dupExtras.length === 1 ? "y" : "ies"} found (same name). Removing keeps the oldest of each and deletes the rest.
        </Alert>
      )}

      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: "grid", placeItems: "center", py: 6 }}><CircularProgress /></Box>
          ) : filtered.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
              No categories yet. Click “Add category” to create one.
            </Typography>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["Image", "Name", "Parent", "ID", ""].map((h) => (
                      <TableCell key={h} sx={{ color: "text.secondary", fontWeight: 700 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell><Thumb r={r} /></TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" fontWeight={600}>{r.name}</Typography>
                          {isDup(r) && <Chip size="small" label="Duplicate" color="warning" variant="outlined" />}
                        </Stack>
                      </TableCell>
                      <TableCell>{parentName(r.parentId)}</TableCell>
                      <TableCell>{r.id}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setConfirm({ open: true, kind: "delete", target: r })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Create / edit dialog */}
      <Dialog open={dialogOpen} onClose={() => !busy && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{form.id ? "Edit category" : "Add category"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" value={form.name} onChange={(e) => onNameChange(e.target.value)}
              error={!!nameError} helperText={nameError} autoFocus fullWidth />

            <ImageInput value={form.image || ""} onChange={(v) => setForm((f) => ({ ...f, image: v }))} label="Category image" />

            <FormControl fullWidth>
              <InputLabel id="parent-label">Parent category (optional)</InputLabel>
              <Select labelId="parent-label" label="Parent category (optional)"
                value={form.parentId === "" ? "" : String(form.parentId)}
                onChange={(e) => setForm((f) => ({ ...f, parentId: e.target.value === "" ? "" : Number(e.target.value) }))}>
                <MenuItem value=""><em>None (top level)</em></MenuItem>
                {parents.filter((p) => p.id !== form.id).map((p) => (
                  <MenuItem key={p.id} value={String(p.id)}>{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={busy || !!nameError || !form.name.trim()}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm dialog (delete / dedupe) */}
      <Dialog open={confirm.open} onClose={() => !busy && setConfirm({ ...confirm, open: false })} maxWidth="xs" fullWidth>
        <DialogTitle>{confirm.kind === "delete" ? "Delete category" : "Remove duplicates"}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirm.kind === "delete"
              ? `Delete "${confirm.target?.name}"? This cannot be undone.`
              : `Remove ${dupExtras.length} duplicate categor${dupExtras.length === 1 ? "y" : "ies"}? The oldest of each name is kept.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm({ ...confirm, open: false })} disabled={busy}>Cancel</Button>
          <Button color="error" variant="contained" disabled={busy}
            onClick={() => confirm.kind === "delete" ? (confirm.target && doDelete(confirm.target)) : removeDuplicates()}>
            {busy ? "Working…" : (confirm.kind === "delete" ? "Delete" : "Remove")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack.sev} variant="filled" onClose={() => setSnack({ ...snack, open: false })}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

const ImagePreview: React.FC<{ url: string }> = ({ url }) => {
  const [bad, setBad] = useState(false);
  const trimmed = url.trim();
  useEffect(() => { setBad(false); }, [trimmed]);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box sx={{ width: 72, height: 72, borderRadius: 1.5, border: "1px solid", borderColor: "divider",
        display: "grid", placeItems: "center", overflow: "hidden", bgcolor: "#f6f8f7", flexShrink: 0 }}>
        {trimmed && !bad ? (
          <Box component="img" src={trimmed} alt="preview" onError={() => setBad(true)}
            sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
        ) : (
          <ImageNotSupportedIcon color="disabled" />
        )}
      </Box>
      <Typography variant="caption" color="text.secondary">
        {trimmed ? (bad ? "Could not load that image URL." : "Preview") : "Paste an image URL to preview it here."}
      </Typography>
    </Box>
  );
};

export default CategoriesPage;
